import React, { useEffect, useMemo, useState } from 'react';

import MerchantSelect from '@components/operator/MerchantSelect';
import ReturnOrderEdit from '@components/shared/return_orders/ReturnOrderEdit';
import PageHeader from '@shared/PageHeader';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorReturnOrderService } from '@hooks/operator/return_orders/useOperatorReturnOrderService';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import { useTranslation } from 'react-i18next';

import operatorInventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  [key: string]: unknown;
}

const ReturnOrderNewContent: React.FC = () => {
  const { t } = useTranslation();
  const { currentMerchant, merchantInventoryTypes } = useOperatorData();
  const merchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo(() => {
    if (!merchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === merchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, merchantId]);

  const [inventoryAttributeDefinitions, setInventoryAttributeDefinitions] =
    useState<InventoryAttributeDefinition[]>([]);

  useEffect(() => {
    if (!merchantId) {
      setInventoryAttributeDefinitions([]);
      return;
    }
    let cancelled = false;
    operatorInventoryAttributeDefinitionService
      .fetchList({ merchant_id: merchantId })
      .then((result: { success?: boolean; data?: InventoryAttributeDefinition[] }) => {
        if (cancelled) return;
        setInventoryAttributeDefinitions(result?.data ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to fetch inventory attribute definitions', error);
        setInventoryAttributeDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  // <MerchantSelect> 已在外層 gate；本元件 mount 時 currentMerchant 必存在。
  // TS narrow（放在所有 hook 之後）。
  if (!merchantId) return null;

  return (
    <div>
      <PageHeader items={[ 'returns', 'returns.list', { label: t('operator.menu.return_orders.new') } ]} />
      <ReturnOrderEdit
        useReturnOrderService={useOperatorReturnOrderService}
        useProductService={useOperatorProductService}
        role="operator"
        merchantId={merchantId}
        inventoryTypes={inventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
      />
    </div>
  );
};

const OperatorReturnOrderNew: React.FC = () => (
  <MerchantSelect>
    <ReturnOrderNewContent />
  </MerchantSelect>
);

export default OperatorReturnOrderNew;
